import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, ChevronLeft, ChevronRight, ImagePlus, Upload, X, Sparkles, Eye, RefreshCw } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LazyImage } from "@/components/ui/lazy-image";
import type { AdStyle } from "./types";
import { cn } from "@/lib/utils";
import { dictionary, Language } from "@/utils/dictionary";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface StylePreviewModalProps {
  allStyles: AdStyle[];
  initialStyle: AdStyle | null;
  open: boolean;
  onClose: () => void;
  selectedStyles: string[];
  styleQuantities?: Record<string, number>;
  onToggle: (id: string) => void;
  onAdd?: (id: string) => void;
  minSelection: number;
  maxSelection: number;
  customReferencePreview?: string;
  onCustomFileSelect?: (file: File) => void;
  onCustomFileRemove?: () => void;
  language: Language;
}

export function   StylePreviewModal({
  allStyles,
  initialStyle,
  open,
  onClose,
  selectedStyles,
  styleQuantities: _styleQuantities,
  onToggle,
  onAdd,
  minSelection,
  maxSelection,
  customReferencePreview,
  onCustomFileSelect,
  onCustomFileRemove,
  language,
}: StylePreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && initialStyle) {
      const idx = allStyles.findIndex((s) => s.id === initialStyle.id);
      if (idx >= 0) setCurrentIndex(idx);
    }
  }, [open, initialStyle, allStyles]);

  const current = allStyles[currentIndex] ?? null;
  const isSelected = current ? selectedStyles.includes(current.id) : false;
  const isCustom = current?.isCustomUpload === true;
  const isMaxReached = selectedStyles.length >= maxSelection;
  const remaining = maxSelection - selectedStyles.length;

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : allStyles.length - 1));
  }, [allStyles.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < allStyles.length - 1 ? prev + 1 : 0));
  }, [allStyles.length]);

  const handleAdd = useCallback(() => {
    if (!current) return;

    if (isCustom) {
      if (isSelected) {
        // Deselect custom
        onToggle(current.id);
        return;
      }
      if (isMaxReached) return;
      onToggle(current.id);
      if (!customReferencePreview) {
        fileInputRef.current?.click();
      }
      return;
    }

    // Already selected → deselect
    if (isSelected) {
      onToggle(current.id);
      return;
    }

    // Max reached → can't add more
    if (isMaxReached) return;

    if (onAdd) {
      onAdd(current.id);
    } else {
      onToggle(current.id);
    }

  }, [current, isCustom, isSelected, isMaxReached, onAdd, onToggle, customReferencePreview]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        alert(dictionary["file-type-not-allowed"]?.[language as Language] || "Solo se permiten archivos JPG, PNG o WEBP");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(dictionary["file-size-limit"]?.[language as Language] || "El archivo no puede superar 5MB");
        return;
      }
      onCustomFileSelect?.(file);
      if (current && !selectedStyles.includes(current.id)) {
        onToggle(current.id);
      }
      e.target.value = "";
    },
    [onCustomFileSelect, current, selectedStyles, onToggle]
  );

  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={handleFileChange}
      />

      <DialogContent className="max-w-xl max-h-[90vh] p-0 overflow-hidden bg-background border border-border/40 shadow-xl rounded-2xl flex flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-col max-h-[90vh] min-h-[50vh]"
          >
            {/* Image area — altura fija para evitar saltos al cambiar de imagen */}
            <div className="relative flex items-center justify-center bg-muted/30 overflow-hidden h-[50vh] min-h-[280px] shrink-0">
              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3 right-3 z-20 h-8 w-8 rounded-full bg-background/90 border border-border/50 flex items-center justify-center hover:bg-background hover:shadow-md transition-all"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4 text-foreground" />
              </button>

              {allStyles.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/95 border border-border/50 flex items-center justify-center hover:bg-background hover:shadow-md transition-all"
                  >
                    <ChevronLeft className="h-4 w-4 text-foreground" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/95 border border-border/50 flex items-center justify-center hover:bg-background hover:shadow-md transition-all"
                  >
                    <ChevronRight className="h-4 w-4 text-foreground" />
                  </button>
                </>
              )}

              {isCustom ? (
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  {customReferencePreview && isSelected ? (
                    <div className="relative w-full h-full flex items-center justify-center group/custom">
                      <img
                        src={customReferencePreview}
                        alt="Referencia personalizada"
                        className="max-w-full max-h-full w-auto h-auto object-contain rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCustomFileRemove?.();
                        }}
                        className="absolute top-1 right-1 h-7 w-7 rounded-full bg-background/90 border border-border/40 flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors shadow-sm z-10"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/custom:opacity-100 transition-opacity bg-black/15 rounded-xl"
                      >
                        <div className="px-4 py-2.5 rounded-xl bg-background/95 text-sm font-medium flex items-center gap-2 shadow-md border border-border/30">
                          <Upload className="h-4 w-4 text-muted-foreground" />
                          {dictionary["change-image"]?.[language as Language] || "Cambiar imagen"}
                        </div>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-6 max-w-sm">
                      {/* 3 explanatory steps */}
                      <div className="flex flex-col gap-3 w-full">
                        {[
                          { icon: <ImagePlus className="h-4 w-4 text-[#5050EA]" strokeWidth={1.5} />, text: "Sube un anuncio de referencia" },
                          { icon: <Eye className="h-4 w-4 text-[#5050EA]" strokeWidth={1.5} />, text: "La IA analiza el estilo visual" },
                          { icon: <RefreshCw className="h-4 w-4 text-[#5050EA]" strokeWidth={1.5} />, text: "Lo replica con tu producto" },
                        ].map((step, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full bg-[#EEF0FF] flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-[#5050EA]">{i + 1}</span>
                            </div>
                            <div className="w-8 h-8 rounded-lg bg-[#F8F7FF] border border-[#E0DDFF]/50 flex items-center justify-center flex-shrink-0">
                              {step.icon}
                            </div>
                            <p className="text-sm text-foreground font-medium">{step.text}</p>
                          </div>
                        ))}
                      </div>

                      {/* Upload button */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files?.[0];
                          if (!file) return;
                          if (!ACCEPTED_TYPES.includes(file.type)) return;
                          if (file.size > MAX_FILE_SIZE) return;
                          onCustomFileSelect?.(file);
                          if (current && !selectedStyles.includes(current.id)) {
                            onToggle(current.id);
                          }
                        }}
                        className="w-full flex flex-col items-center justify-center gap-3 py-6 border-2 border-dashed border-border/60 rounded-2xl hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer"
                      >
                        <div className="p-3 rounded-xl bg-[#EEF0FF]">
                          <Sparkles className="h-5 w-5 text-[#5050EA]" strokeWidth={1.5} />
                        </div>
                        <div className="text-center space-y-0.5">
                          <p className="text-sm font-semibold text-foreground">{dictionary["upload-your-reference"]?.[language as Language] || "Sube tu referencia"}</p>
                          <p className="text-xs text-muted-foreground">
                            {dictionary["drag-or-click"]?.[language as Language] || "Arrastra o haz clic"} · JPG, PNG · {dictionary["max-file-size"]?.[language as Language] || "Max 5MB"}
                          </p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <LazyImage
                    src={current.coverImage}
                    alt={current.name}
                    className="max-w-full max-h-full w-auto h-auto object-contain rounded-xl"
                    wrapperClassName="flex items-center justify-center w-full h-full min-h-0"
                  />
                </div>
              )}
            </div>

            {/* Selection counter */}
            <div className="px-5 pt-3">
              {isMaxReached ? (
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex-shrink-0">
                    !
                  </div>
                  <p className="text-xs text-amber-700 font-medium">
                    {(dictionary["max-templates-reached"]?.[language as Language] || "Llegaste al máximo de {n} plantillas por generación").replace("{n}", String(maxSelection))}
                  </p>
                  <span className="ml-auto text-xs font-bold text-amber-700 tabular-nums">{selectedStyles.length}/{maxSelection}</span>
                </div>
              ) : selectedStyles.length > 0 ? (
                <div className="flex items-center gap-2 rounded-lg bg-[#EEF0FF] px-3 py-2">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#5050EA] text-white text-[10px] font-bold flex-shrink-0">
                    {selectedStyles.length}
                  </div>
                  <p className="text-xs text-[#5050EA] font-medium">
                    {(dictionary["n-templates-selected"]?.[language as Language] || "{n} plantilla(s) seleccionada(s)").replace("{n}", String(selectedStyles.length))}
                  </p>
                  <span className="ml-auto text-xs font-bold text-[#5050EA] tabular-nums">{selectedStyles.length}/{maxSelection}</span>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t-0 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <h3 className="text-sm font-bold text-foreground truncate">
                  {current.name}
                </h3>
                <span className="text-[11px] text-muted-foreground tabular-nums flex-shrink-0">
                  {currentIndex + 1}/{allStyles.length}
                  {" · "}
                  {selectedStyles.length}/{minSelection}-{maxSelection}
                </span>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <Button
                  size="sm"
                  onClick={handleAdd}
                  disabled={!isSelected && isMaxReached}
                  className={cn(
                    "gap-1.5 rounded-full px-4 h-9 text-xs font-semibold transition-all",
                    isSelected
                      ? "bg-red-500/10 text-red-600 border border-red-200 hover:bg-red-500/20"
                      : "bg-foreground text-background hover:bg-foreground/90"
                  )}
                >
                  {isCustom ? (
                    isSelected ? (
                      <>
                        <X className="h-3.5 w-3.5" />
                        {dictionary["remove"]?.[language as Language] || "Quitar"}
                      </>
                    ) : (
                      <>
                        <ImagePlus className="h-3.5 w-3.5" />
                        {dictionary["upload-image"]?.[language as Language] || "Subir imagen"}
                      </>
                    )
                  ) : (
                    <>
                      {isSelected ? (
                        <>
                          <X className="h-3.5 w-3.5" />
                          {dictionary["remove"]?.[language as Language] || "Quitar"}
                        </>
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5" />
                          {dictionary["add"]?.[language as Language] || "Agregar"}
                        </>
                      )}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Thumbnail strip */}
            {allStyles.length > 1 && (
              <div className="px-5 pb-4 pt-0">
                <div className="flex gap-2 overflow-x-auto scrollbar-hide py-0.5">
                  {allStyles.map((style, index) => {
                    const isCurrent = index === currentIndex;
                    const isStyleSelected = selectedStyles.includes(style.id);
                    const isThumbCustom = style.isCustomUpload;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => setCurrentIndex(index)}
                        className={cn(
                          "relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all duration-150",
                          isCurrent
                            ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
                            : "opacity-40 hover:opacity-80 border border-border/30"
                        )}
                      >
                        {isThumbCustom ? (
                          customReferencePreview ? (
                            <img src={customReferencePreview} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted/20">
                              <ImagePlus className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                            </div>
                          )
                        ) : (
                          <img src={style.coverImage} alt="" className="w-full h-full object-cover" />
                        )}
                        {isStyleSelected && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
